import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Monitor, Smartphone, Tablet, HelpCircle } from 'lucide-react';
import { DeviceBreakdown as DeviceBreakdownType } from '../../types';

interface DeviceBreakdownProps {
  data: DeviceBreakdownType | null;
  loading: boolean;
}

const COLORS = {
  desktop: 'hsl(217, 91%, 60%)',
  mobile: 'hsl(142, 71%, 45%)',
  tablet: 'hsl(262, 83%, 58%)',
  unknown: 'hsl(220, 9%, 46%)'
};

const ICONS = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  unknown: HelpCircle
};

export function DeviceBreakdown({ data, loading }: DeviceBreakdownProps) {
  const chartData = useMemo(() => {
    if (!data) return [];

    const total = data.desktop + data.mobile + data.tablet + data.unknown;
    if (total === 0) return [];

    return [
      { name: 'Desktop', value: data.desktop, color: COLORS.desktop },
      { name: 'Mobile', value: data.mobile, color: COLORS.mobile },
      { name: 'Tablet', value: data.tablet, color: COLORS.tablet },
      { name: 'Desconhecido', value: data.unknown, color: COLORS.unknown }
    ].filter((item) => item.value > 0);
  }, [data]);

  const total = data ? data.desktop + data.mobile + data.tablet + data.unknown : 0;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Dispositivos</CardTitle>
          <CardDescription>Distribuição por tipo de dispositivo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Dispositivos</CardTitle>
          <CardDescription>Distribuição por tipo de dispositivo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Sem dados de dispositivo
          </div>
        </CardContent>
      </Card>
    );
  }

  const deviceStats = [
    { key: 'desktop', label: 'Desktop', value: data.desktop, icon: ICONS.desktop, color: COLORS.desktop },
    { key: 'mobile', label: 'Mobile', value: data.mobile, icon: ICONS.mobile, color: COLORS.mobile },
    { key: 'tablet', label: 'Tablet', value: data.tablet, icon: ICONS.tablet, color: COLORS.tablet },
    { key: 'unknown', label: 'Desconhecido', value: data.unknown, icon: ICONS.unknown, color: COLORS.unknown }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dispositivos</CardTitle>
        <CardDescription>Distribuição por tipo de dispositivo</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => {
                  const numValue = typeof value === 'number' ? value : 0;
                  return [`${numValue} (${((numValue / total) * 100).toFixed(1)}%)`, ''];
                }}
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          {/* Stats List */}
          <div className="space-y-4">
            {deviceStats.map((stat) => {
              const percentage = total > 0 ? (stat.value / total) * 100 : 0;
              const Icon = stat.icon;

              return (
                <div key={stat.key} className="flex items-center gap-4">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${stat.color}20` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: stat.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">{stat.label}</span>
                      <span className="text-sm text-muted-foreground">
                        {stat.value.toLocaleString('pt-BR')} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: stat.color
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
